// ROLE_CATALOG + MEMBER_SCOPES — the role/scope vocabulary v3 kept as a `ref([...])` literal
// inside the component (`UsersPermissions.vue:384-390`, i.e. `:161-167` in the ticket's numbering)
// and then indexed POSITIONALLY (`roles.value[1]` / `roles.value[0]`, `:531-532, 555-556`). A role
// added to that array silently re-assigned every dashboard grant. Here roles are addressed BY NAME
// through total `Record<MemberRole, …>` maps — adding a role is a COMPILE error until it is
// labelled and scoped — and the catalog carries i18n key paths instead of English (§13).
//
// The role/scope matrix is the legacy's `activeRoles` predicate (`UserPermissionModal.vue:300-308`)
// read literally: `owner` is never assignable; at DASHBOARD scope only `viewer` + `viewer_plus`
// (`dash` / `only_dash`); everywhere else every role except `viewer_plus` (`!only_dash`).
//
// ⚠ `viewer_plus` IS NOT REPRESENTABLE AT ACCOUNT SCOPE — a BACKEND fact, not a UI preference.
// `format_role_for_frontend` (`accounts/helper.py:63-71`) maps `>=30 → 'admin'`, `>=20 → 'editor'`,
// else `'viewer'`, and `get_account_permissions.get_permissions` is the ONLY role source on the
// `account_users` container — so VIEWER_PLUS(15) and VIEWER(10) are the same byte by the time an
// account-scoped row reaches the client. Offering it at account scope would write a grant that
// reads back as `viewer` on the very next load. Hence:
//   • `assignableScopes` excludes `'account'` for `viewer_plus` (matching v3's `only_dash`), and
//   • the catalog still carries a LABEL for it, because the workspace/dashboard containers expose
//     the raw int and CAN yield `viewer_plus` on read (`service/memberList.ts` prefers `perm.role`).
// Assignability and displayability are deliberately two different things. A lossless account-scope
// role is BE-22-08's ask; until it lands this asymmetry is the honest model.
import {
  MEMBER_SCOPE_LEVEL,
  type MemberRole,
  type MemberScope,
  type MemberScopeLevel,
} from './member.types'

export interface MemberRoleDescriptor {
  role: MemberRole
  /** i18n key path in 22's namespace — never a literal (contract §13). */
  labelKey: string
  /** The scopes at which this role may be GRANTED. Empty ⇒ never assignable (`owner`). */
  assignableScopes: readonly MemberScope[]
}

export interface MemberScopeDescriptor {
  scope: MemberScope
  /** v3's `level.value` (0/1/2), named. Kept so a caller can order scopes broadest-first. */
  level: MemberScopeLevel
  labelKey: string
}

/** Total by construction: `satisfies Record<MemberRole, …>` fails the build on an unlabelled role. */
const ROLE_LABEL_KEY = {
  viewer: 'workspaceSettings.permissions.role.viewer',
  viewer_plus: 'workspaceSettings.permissions.role.viewerPlus',
  editor: 'workspaceSettings.permissions.role.editor',
  admin: 'workspaceSettings.permissions.role.admin',
  owner: 'workspaceSettings.permissions.role.owner',
} as const satisfies Record<MemberRole, string>

const ROLE_ASSIGNABLE_SCOPES = {
  viewer: ['account', 'workspace', 'dashboard'],
  viewer_plus: ['dashboard'],
  editor: ['account', 'workspace'],
  admin: ['account', 'workspace'],
  // The owner is a property of the ACCOUNT, not a grant: it moves only through
  // `TransferOwnershipFlow` (`PATCH /auth/change-ownership/{a}`), never through a role picker.
  owner: [],
} as const satisfies Record<MemberRole, readonly MemberScope[]>

const SCOPE_LABEL_KEY = {
  account: 'workspaceSettings.permissions.scope.account',
  workspace: 'workspaceSettings.permissions.scope.workspace',
  dashboard: 'workspaceSettings.permissions.scope.dashboard',
} as const satisfies Record<MemberScope, string>

/** Broadest scope first — the order a role/scope picker offers them in (v3's level 0 → 1 → 2). */
const SCOPE_ORDER: readonly MemberScope[] = ['account', 'workspace', 'dashboard']

export const ROLE_CATALOG: readonly MemberRoleDescriptor[] = (
  Object.keys(ROLE_LABEL_KEY) as MemberRole[]
).map((role) => ({
  role,
  labelKey: ROLE_LABEL_KEY[role],
  assignableScopes: ROLE_ASSIGNABLE_SCOPES[role],
}))

export const MEMBER_SCOPES: readonly MemberScopeDescriptor[] = SCOPE_ORDER.map((scope) => ({
  scope,
  level: MEMBER_SCOPE_LEVEL[scope],
  labelKey: SCOPE_LABEL_KEY[scope],
}))

/** The label key for a role that came off the wire. Total — every `MemberRole` has an entry. */
export function memberRoleLabelKey(role: MemberRole): string {
  return ROLE_LABEL_KEY[role]
}

export function memberScopeLabelKey(scope: MemberScope): string {
  return SCOPE_LABEL_KEY[scope]
}

/** The roles a caller may offer at `scope` — the v4 form of v3's `activeRoles` computed. */
export function assignableRoles(scope: MemberScope): MemberRoleDescriptor[] {
  return ROLE_CATALOG.filter((entry) => entry.assignableScopes.includes(scope))
}
